import * as React from "react";
import i18n from "i18next";
import { withTranslation, WithTranslation } from "react-i18next";
import { Row, Col, Form, FormInstance } from "antd";
import { FormCard } from "Components/Form/FormCard";
import { Select, Checkbox, Button } from "antd";
import { userInfoStore } from "Stores/UserInfoStore";
import { observer } from "mobx-react";
import { DisplayAPIError } from "Components/General/DisplayAPIError";
import { userSettingsStore } from "Stores/UserSettingsStore";
import { notificationSuccess, notificationError } from "utils/libHelpers/AntdNotification";
import { I18N_NS } from "Config/i18n";
import { User2FASelect } from "./User2FASelect";
import { dayjs } from "Config/dayjs";
import { RichEditor, RichEditorInstance } from "Pages/Project/Overview/Components/RichEditor/RichEditor";

interface LanguageMap {
  /**
   * Name of Language
   */
  name: string;
  /**
   * Official LanguageCode
   */
  code: string;
}

export const ChangeLanguage = withTranslation(I18N_NS.USER_SETTINGS)(
  observer(
    class extends React.Component<WithTranslation, null> {
      private languages: Array<LanguageMap> = [
        {
          name: "Deutsch",
          code: "de",
        },
        {
          name: "English",
          code: "en",
        },
      ];

      render() {
        const { t } = this.props;
        const langs = this.languages.map((val: LanguageMap) => ({
          label: val.name,
          value: val.code.toUpperCase(),
        }));

        return (
          <Form.Item label={t("Available Languages")}>
            <Select
              value={`${userSettingsStore.data.language}`.toUpperCase()}
              allowClear={false}
              onChange={(language: API_USERSETTINGS_LANGUAGE) =>
                userSettingsStore.update({
                  language,
                })
              }
              options={langs}
            />
          </Form.Item>
        );
      }
    }
  )
);

interface GeneralFormProps extends WithTranslation {}

export const GeneralUserSettingsForm = withTranslation(I18N_NS.USER_SETTINGS)(
  observer(
    class extends React.Component<GeneralFormProps> {
      editor = React.createRef<RichEditorInstance>();
      formRef: React.RefObject<FormInstance> = React.createRef();

      render() {
        const { t } = this.props;
        const { update } = userSettingsStore;
        const { sendMailCopyToMe, showMailSignature, mailSignature, layoutType, dashboardLayout } =
          userSettingsStore.data;

        return (
          <DisplayAPIError store={userSettingsStore} state="saveSettingsState">
            <Form className="user-settings-form" ref={this.formRef} layout="vertical">
              <div className="wrapper">
                <Row justify="space-between" gutter={8}>
                  <Col xs={24} md={12}>
                    <FormCard title={t("Language")}>
                      <ChangeLanguage />
                    </FormCard>
                  </Col>
                  <Col xs={24} md={12}>
                    <FormCard title={t("Dashboard")}>
                      <Form.Item label={t("Default layout")}>
                        <Select
                          defaultValue={dashboardLayout || "LIST"}
                          onChange={(value) => update({ dashboardLayout: value })}
                          options={[
                            { label: t("Tiles"), value: "TILES" },
                            { label: t("List"), value: "LIST" },
                            { label: t("Compact", { ns: I18N_NS.PROJECT }), value: "COMPACT" },
                          ]}
                        />
                      </Form.Item>
                    </FormCard>
                  </Col>
                </Row>
                <FormCard title={t("Login option (2. Factor)")}>
                  <User2FASelect unifiedAccount={userInfoStore.loginInfo.unifiedAccount} />
                </FormCard>
                <FormCard title={t("Communication")}>
                  <Form.Item label={t("Default layout")}>
                    <Select
                      style={{ width: 250 }}
                      defaultValue={layoutType || "SPLIT"}
                      onChange={(value) => update({ layoutType: value })}
                      options={[
                        { label: t("Split"), value: "SPLIT" },
                        { label: t("List"), value: "LIST" },
                      ]}
                    />
                  </Form.Item>
                  <div>
                    <Checkbox
                      checked={sendMailCopyToMe}
                      onChange={() => {
                        update({
                          sendMailCopyToMe: !sendMailCopyToMe,
                        });
                      }}
                    >
                      {t("Receive a copy of my mails")}
                    </Checkbox>
                  </div>
                  <div>
                    <Checkbox
                      checked={showMailSignature}
                      onChange={() => update({ showMailSignature: !showMailSignature })}
                    >
                      {t("Email signature")}
                    </Checkbox>
                  </div>
                  {showMailSignature && (
                    <div style={{ marginTop: 10 }}>
                      <RichEditor initialValue={mailSignature ? mailSignature : ""} ref={this.editor} />
                    </div>
                  )}
                </FormCard>
              </div>

              <div className="text-right">
                <Button onClick={this.handleSubmit} type="primary" loading={userSettingsStore.state === "LOADING"}>
                  {t("Save")}
                </Button>
              </div>
            </Form>
          </DisplayAPIError>
        );
      }

      private handleChangeLanguage() {
        let newLanguage = userSettingsStore.data.language.toLowerCase();
        userInfoStore.setLanguage(newLanguage);
        if (newLanguage === "en") {
          newLanguage = "en-gb";
        }
        i18n.changeLanguage(newLanguage);
        dayjs.locale(newLanguage);
        console.log(dayjs.locale());
      }

      private handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const { t } = this.props;
        this.formRef.current
          .validateFields()
          .then(async (values) => {
            const mailSignature = this.editor ? this.editor.current?.getHtml() : null;
            userSettingsStore.update({ ...values, mailSignature });
            (async () => {
              try {
                await userSettingsStore.saveSettings();
                notificationSuccess(t("Success"), t("Saved Settings"));
                this.handleChangeLanguage();
              } catch (err) {
                notificationError(t("Error"), t("Settings could not be saved"));
              }
            })();
          })
          .catch((error) => {
            throw error;
          });
      };
    }
  )
);
